import { graphql } from '@api/generated';

import { getClient } from '@api/client';
import {
  CallRecording,
  CallRecordingSettingsOptions,
  CallRecordingStatus,
} from '@api/generated/graphql';

const GET_RECORDING_STATUS_TIMEOUT_IN_MS = 10_000;

const GET_RECORDING_QUERY = graphql(`
  query recordingStatus($kazooCallId: String!) {
    recordingStatus(kazooCallId: $kazooCallId) {
      status
      settings
      announceRecording
    }
  }
`);

// Settings whose status can only ever be NO_RECORDING (RecordingControlHandler
// in telecom-pcs never auto-starts a recording for these) - once settings
// resolves to one of these, status will not change on its own and polling can
// stop immediately. Everything else (including any future setting PCS adds)
// is treated as "could still start recording" so polling continues instead of
// silently giving up.
const SETTINGS_THAT_NEVER_AUTO_START_RECORDING = [
  CallRecordingSettingsOptions.Manual,
  CallRecordingSettingsOptions.Never,
  CallRecordingSettingsOptions.Unknown,
];

function isRecordingStatusTerminal(recordingStatus: CallRecording): boolean {
  if (recordingStatus.settings === CallRecordingSettingsOptions.Unset) {
    return false;
  }

  if (recordingStatus.status !== CallRecordingStatus.NoRecording) {
    return true;
  }

  return SETTINGS_THAT_NEVER_AUTO_START_RECORDING.includes(
    recordingStatus.settings,
  );
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Waits until the given (0-based) attempt is due: at its scheduled time while
 * the `attemptTimesMs` schedule lasts, then with linear backoff.
 */
async function waitForAttempt(
  attempt: number,
  attemptTimesMs: number[],
  startTime: number,
): Promise<void> {
  if (attempt < attemptTimesMs.length) {
    await delay(startTime + attemptTimesMs[attempt] - Date.now());
  } else if (attempt > 0) {
    await delay(1000 * attempt); // retry with linear backoff
  }
}

/**
 * Resolves a failed final attempt: an already-terminal result is still good,
 * so don't discard it because this attempt errored. With nothing usable yet,
 * this is a genuine failure.
 */
function resolveErroredAttempt(
  lastObservedStatus: CallRecording | undefined,
  errorMessage: string,
): CallRecording {
  if (lastObservedStatus && isRecordingStatusTerminal(lastObservedStatus)) {
    return lastObservedStatus;
  }
  throw new Error(errorMessage);
}

export type GetRecordingStatusOptions = {
  /**
   * Times, in ms since the call to `getRecordingStatus`, at which to make the
   * first attempts (e.g. `[1000, 3000, 6000]`). Right after a transfer, Kazoo
   * can take several seconds to report the transferee's real recording
   * status, and PCS can meanwhile keep answering with the transferer's
   * already-resolved (and therefore terminal-looking) settings/status - so
   * nothing is requested up front, and a terminal-looking result is not
   * trusted until every scheduled attempt has been made. Once the schedule is
   * exhausted the normal linear backoff takes over.
   */
  attemptTimesMs?: number[];
  /**
   * Called with the status observed by each scheduled attempt that does not
   * end the poll, so a caller can show it while later attempts are still
   * pending (as a transfer's status is corrected over several seconds). Never
   * called for the status the poll resolves with, and never called for
   * attempts made outside the `attemptTimesMs` schedule.
   */
  onIntermediateStatus?: (recordingStatus: CallRecording) => void;
};

/**
 * Fetches the recording status for the provided `kazooCallId`, retrying with
 * linear backoff for up to 10 seconds while the settings are still resolving,
 * or while status is NO_RECORDING but the resolved setting could still start
 * a recording (e.g. the call hasn't been answered yet), or while attempts
 * remain on the `attemptTimesMs` schedule.
 *
 * If the timeout is reached, returns the last observed status (e.g. a
 * still-accurate NO_RECORDING) rather than discarding it. Returns `undefined`
 * only if no status was ever observed (e.g. the query never returned data).
 * @param kazooCallId - remoteCallId for a call
 * @param options - optional fixed attempt schedule, used for transferred calls
 */
async function getRecordingStatus(
  kazooCallId: string,
  options?: GetRecordingStatusOptions,
): Promise<CallRecording | undefined> {
  const attemptTimesMs = options?.attemptTimesMs ?? [];
  const startTime = Date.now();
  let attempts = 0;
  let lastObservedStatus: CallRecording | undefined;

  while (true) {
    await waitForAttempt(attempts, attemptTimesMs, startTime);

    const { data, error } = await getClient().query({
      query: GET_RECORDING_QUERY,
      variables: {
        kazooCallId,
      },
      fetchPolicy: 'network-only',
    });

    if (error) {
      // an errored scheduled attempt does not end the poll while later ones
      // remain: any earlier reading may still be the transferer's, so move on
      // to the next scheduled attempt instead of trusting it
      if (attempts < attemptTimesMs.length - 1) {
        attempts += 1;
        continue;
      }
      return resolveErroredAttempt(lastObservedStatus, error.message);
    }

    attempts += 1;
    lastObservedStatus = data?.recordingStatus ?? lastObservedStatus;

    const isScheduleDone = attempts >= attemptTimesMs.length;
    const isSettled =
      !!lastObservedStatus && isRecordingStatusTerminal(lastObservedStatus);
    const isTimedOut =
      Date.now() - startTime >= GET_RECORDING_STATUS_TIMEOUT_IN_MS;

    if (isScheduleDone && (isSettled || isTimedOut)) {
      return lastObservedStatus;
    }

    if (data?.recordingStatus && attempts <= attemptTimesMs.length) {
      options?.onIntermediateStatus?.(data.recordingStatus);
    }
  }
}

export default getRecordingStatus;
